import * as React from 'react'
import * as O from 'fp-ts/lib/Option'
import { Link } from 'react-router-dom'
import { faPenAlt } from '@fortawesome/pro-light-svg-icons'
import styled from 'styled-components'

import {
  View,
  Section,
  StatusBorder
} from '@espoon-voltti/react-components/layout'
import { Button, Buttons } from '@espoon-voltti/react-components/elements'
import { Checkbox } from '@espoon-voltti/react-components/elements/form'
import { Collapsible } from '@espoon-voltti/react-voltti/components/collapsible'
import { closeWithSideEffect } from '@espoon-voltti/react-voltti/components/modal'

import { UserConfig } from '@espoon-voltti/react-voltti/components/modal'

import { Org } from '@espoon-voltti/nuora-domain/applicants'
import {
  GeneralGrantPaperFormData,
  GrantPeriod
} from '@espoon-voltti/nuora-domain/grants'

import { AuthMetadata } from '@espoon-voltti/nuora-app-common/services/auth'
import { GlobalModal } from '@espoon-voltti/nuora-app-common/globals'

import { Breadcrumb } from '../../../components'
import { t } from '../../../i18n'
import {
  SigningApp,
  SigningData,
  ValidDraftApp,
  GeneralGrantFormData,
  ProjectGrantFormData,
  ValidGeneralGrantDraftApp
} from '../../../domain'
import { localizedPathnames } from '../../../utils/localized-pathname'

import { Signers } from './signers'
import { pipe } from 'fp-ts/lib/pipeable'
import { maybeProjectGrantForm } from '../support'
import { GeneralGrantAppSigning } from './general-grant'
import { ProjectGrantSigning } from './project-grant'
import { setModalFocus } from '../../../utils/modal-focus'

const CheckboxWrapper = styled.div`
  label {
    display: flex;
    flex-direction: row;
    justify-content: center;
    max-width: 600px;
    margin: auto;
  }
`

const getDisplayName = (
  x: GeneralGrantFormData | ProjectGrantFormData | GeneralGrantPaperFormData
): string => {
  switch (x.type) {
    case 'PROJECT_GRANT':
      return x.event.name ?? ''
    case 'GENERAL_GRANT':
    case 'GENERAL_GRANT_PAPER':
      return t(['views', 'generalGrant', 'form', 'title'])
    default:
      return ''
  }
}

interface AppSigningProps {
  app: ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp
  grantPeriod: GrantPeriod
  org: Org
  user: AuthMetadata
  onClose: () => unknown
  onDelete: () => unknown
  onSign: (
    app: ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp
  ) => unknown
}

export const AppSigning: React.FC<AppSigningProps> = (p) => {
  const [verified, setVerified] = React.useState<boolean>(false)

  const signingData = p.app.state === 'DRAFT' ? null : p.app.signing
  const userAlreadySigned = signingData
    ? hasUserSigned(p.user, signingData)
    : false

  const toggleVerified = () => setVerified(!verified)
  const handleSign = () => {
    GlobalModal.get().showCancellable(
      isLastSignerFor(p.app.state, p.app.applicant.org)
        ? sendAfterSignConfig()
        : awaitAfterSignConfig(),
      closeWithSideEffect(() => p.onSign(p.app))
    )
    setModalFocus()
  }

  return (
    <View
      dataQa="appSigning"
      title={getDisplayName(p.app.form)}
      subtitle={p.app.applicant.org.name}
      status={t(['common', 'statuses', p.app.state])}
      breadcrumbs={
        <Breadcrumb
          route={[
            {
              label: t(['breadcrumb', 'home']),
              link: localizedPathnames.home(p.org.id)
            },
            { label: t(['breadcrumb', p.grantPeriod.type]), link: '#' }
          ]}
        />
      }
    >
      <StatusBorder status={'new'} />
      {pipe(
        p.app.form,
        maybeProjectGrantForm,
        O.fold(
          () =>
            pipe(
              p.app.form,
              maybeGeneralGrantForm,
              O.map((x) => (
                <GeneralGrantAppSigning
                  key={p.app.id}
                  form={x}
                  applicant={p.app.applicant}
                />
              )),
              O.toNullable
            ),
          (x) => (
            <ProjectGrantSigning
              key={p.app.id}
              form={x}
              applicant={p.app.applicant}
            />
          )
        )
      )}

      <Section>
        <Collapsible
          dataQa={'signing'}
          title={t(['views', 'form', 'signing', 'title'])}
          icon={faPenAlt}
          open={true}
        >
          <Section>
            {t(['views', 'signing', 'org', 'signingInfo'])}
            <a target="_blank" href={t(['views', 'signing', 'link', 'url'])}>
              {t(['views', 'signing', 'link', 'label'])}
            </a>
            {t(['views', 'signing', 'org', 'signingInfo2'])}
          </Section>

          <Signers
            representatives={p.app.applicant.org.representatives}
            signingData={signingData}
          />
        </Collapsible>

        {!userAlreadySigned && (
          <Section>
            <CheckboxWrapper>
              <label>
                <Checkbox
                  dataQa={'verifyInfo'}
                  name="checked"
                  label={''}
                  checked={verified}
                  onChange={toggleVerified}
                />
                {t(['views', 'signing', 'org', 'check'])}
              </label>
            </CheckboxWrapper>
          </Section>
        )}
      </Section>
      <Buttons centered>
        <Button width={'wide'} dataQa={'delete'} onClick={p.onDelete}>
          {t(['views', 'form', 'delete', 'action'])}
        </Button>

        <Button
          width={'wide'}
          primary={userAlreadySigned}
          dataQa={'close'}
          onClick={p.onClose}
        >
          {t(['views', 'signing', 'close'])}
        </Button>

        {p.app.state === 'DRAFT' ? (
          <Link to={localizedPathnames.application(p.app.id)}>
            <Button width={'wide'} dataQa={'edit'}>
              {t(['views', 'signing', 'edit'])}
            </Button>
          </Link>
        ) : null}

        {!userAlreadySigned && (
          <Button
            width={'wide'}
            dataQa={'sign'}
            primary
            onClick={verified ? handleSign : showNotVerifiedAlert}
          >
            {t(['views', 'signing', 'sign'])}
          </Button>
        )}
      </Buttons>
    </View>
  )
}

const hasUserSigned = (user: AuthMetadata, signing: SigningData): boolean =>
  signing.signers.find((x) => x.ssnHash === user.ssnHash) != null

const showNotVerifiedAlert = () => {
  GlobalModal.get().show({
    title: t(['views', 'signing', 'org', 'notConfirmed', 'title']),
    text: t(['views', 'signing', 'org', 'notConfirmed', 'text'])
  })
  setModalFocus()
}

const isLastSignerFor = (state: 'DRAFT' | 'SIGNING', org: Org): boolean =>
  state === 'SIGNING' || org.requiredSignersAmount === 1

const sendAfterSignConfig = (): UserConfig => ({
  dataQa: 'sendAfterSign',
  title: t(['views', 'signing', 'confirm', 'title']),
  text: t(['views', 'signing', 'confirm', 'text', 'sendAfterSign']),
  type: 'action',
  confirmButtonLabel: t(['views', 'signing', 'confirm', 'controls', 'confirm']),
  cancelButtonLabel: t(['views', 'signing', 'confirm', 'controls', 'cancel'])
})

const awaitAfterSignConfig = (): UserConfig => ({
  dataQa: 'awaitAfterSign',
  title: t(['views', 'signing', 'confirm', 'title']),
  text: t(['views', 'signing', 'confirm', 'text', 'awaitAfterSign']),
  type: 'action',
  confirmButtonLabel: t(['views', 'signing', 'confirm', 'controls', 'confirm']),
  cancelButtonLabel: t(['views', 'signing', 'confirm', 'controls', 'cancel'])
})

export const maybeGeneralGrantForm = (
  x: ProjectGrantFormData | GeneralGrantFormData | GeneralGrantPaperFormData
): O.Option<GeneralGrantFormData> =>
  x.type === 'GENERAL_GRANT' ? O.some(x) : O.none
